import { FunctionComponent } from 'preact';
import { useSelector, useDispatch } from 'react-redux';
import { css } from '../../../common/styles/styles.utils';
import { playerDataSelector } from '../../../common/store/selectors/playerData.selector';
import { useStyles } from '../../../common/hooks/useStyles';
import {
  changePendingAdStatus,
  pauseVideo,
  playVideo,
  resumeVideo,
  setUserInteraction,
} from '../store/controlsPlugin.actions';
import { Icon } from '../../../common/components/icon/icon';
import { adsDataSelector } from '../../../common/store/selectors/adsData.selector';
import { splitViewSelector } from '../../../common/store/selectors/splitView.selector';
import { getPauseCircleId, getPlayCircleId } from '../htmlElements/htmlElementsIdGetter';
import { COMMON_ICON_TYPES } from '../../../common/components/icon/iconMapper';
import { CONTROLS_ICON_TYPES } from '../icon/iconMapper';
import { discoverySelector } from '../../../common/store/selectors/discovery.selector';
import { brandingDataSelector } from '../../../common/store/selectors/brandingData.selector';
import { getCirclePlayPauseButtonStyleObject } from './circlePlayPauseButton.style';

const PLAY_ARIA_LABEL = 'Play';
const PAUSE_ARIA_LABEL = 'Pause';

export const CirclePlayPauseButton: FunctionComponent = () => {
  const dispatch = useDispatch();
  const playbackMethod = useSelector(playerDataSelector.playbackMethod);
  const videoTagStatus = useSelector(playerDataSelector.videoTagStatus);
  const isVastAd = useSelector(adsDataSelector.isVastAd);
  const adStatus = useSelector(adsDataSelector.adStatus);
  const adBreak = useSelector(adsDataSelector.adBreak);
  const playerSize = useSelector(playerDataSelector.playerSize);
  const splitViewRatio = useSelector(splitViewSelector.splitViewRatio);
  const playerUniqueId = useSelector(playerDataSelector.playerInstanceUniqueId);
  const showSkippableContent = useSelector(discoverySelector.showSkippableContent);
  const brandingColor = useSelector(brandingDataSelector.brandingColor);
  const showControls = useSelector(playerDataSelector.showControls);
  const controlsDisplay = useSelector(brandingDataSelector.controlsDisplay);
  const loadingPlayer = useSelector(playerDataSelector.loadingPlayer);

  const hideBlurPlayPause = playerSize === 'small' && showSkippableContent;
  const showAdControls = isVastAd && adBreak;
  const clickToPlayMode = videoTagStatus === 'idle' && playbackMethod === 'none' && !showAdControls && !loadingPlayer;
  const showPlayButton = clickToPlayMode || ((showAdControls ? adStatus === 'paused' : (videoTagStatus === 'paused' || videoTagStatus === 'seeking')) && showControls);
  const showPauseButton = (showAdControls ? adStatus === 'playing' : videoTagStatus === 'playing') && showControls;
  const viewDisplay = controlsDisplay === 'viewDisplay';
  const smallButton = playerSize === 'extraSmall';
  const playIcon = smallButton ? CONTROLS_ICON_TYPES.PLAY_XS_BLUR : COMMON_ICON_TYPES.PLAY_BLUR;
  const pauseIcon = smallButton ? CONTROLS_ICON_TYPES.PAUSE_XS_BLUR : CONTROLS_ICON_TYPES.PAUSE_BLUR;

  const styles = useStyles(getCirclePlayPauseButtonStyleObject, [playerSize, clickToPlayMode, showPlayButton,
    showPauseButton, splitViewRatio, hideBlurPlayPause, showAdControls, viewDisplay]);
  const { playButtonStyle, pauseButtonStyle, containerStyle } = styles;

  const onPlay = (play: () => void): void => {
    if (showAdControls) {
      dispatch(changePendingAdStatus('playing'));
    } else {
      dispatch(play());
      dispatch(setUserInteraction('play'));
    }
  };

  const onPlayClicked = (): void => {
    if (videoTagStatus === 'idle') {
      onPlay(playVideo);
    } else {
      onPlay(resumeVideo);
    }
  };

  const onPauseClicked = (): void => {
    if (showAdControls) {
      dispatch(changePendingAdStatus('paused'));
    } else {
      dispatch(pauseVideo());
      dispatch(setUserInteraction('pause'));
    }
  };

  const iconProps = {
    icon: showPlayButton ? playIcon : pauseIcon,
    style: showPlayButton ? playButtonStyle : pauseButtonStyle,
    onClick: showPlayButton ? onPlayClicked : onPauseClicked,
    color: brandingColor,
    id: showPlayButton ? getPlayCircleId(playerUniqueId) : getPauseCircleId(playerUniqueId),
    ariaLabel: showPlayButton ? PLAY_ARIA_LABEL : PAUSE_ARIA_LABEL,
  };

  return (
    <div style={css(containerStyle)}>
      <Icon {...iconProps} />
    </div>
  );
};
